/**
 * ProductDetailPage chunked-edit modal schemas (S1.4).
 *
 * Error messages are i18n KEYS translated at display time (house schema
 * convention — keys exist in BOTH en + zh products.json). Tier-2 readiness
 * fields stay OPTIONAL to save: readiness gates 產品就緒 on read as enrichment
 * only (create-8 hard-require lives in the wizard schemas), never the save
 * (two-tier OQ-ME2 contract — col-J 74 Must; never col-K as freeze).
 */

import { z } from 'zod';

const optionalNonNegative = z
  .number()
  .min(0, 'products:validation.nonNegative')
  .nullable()
  .optional();

/** Model-year style input (S2.1 — extract year / vehicle start/end year). */
const optionalYear = z
  .number()
  .int('products:validation.yearInvalid')
  .min(1950, 'products:validation.yearInvalid')
  .max(2100, 'products:validation.yearInvalid')
  .nullable()
  .optional();

export const editMainInfoSchema = z.object({
  displayName: z.string().trim().min(1, 'products:validation.displayNameRequired'),
  nameZh: z.string().trim().optional(),
  stockUnit: z.string().min(1, 'products:validation.stockUnitRequired'),
  purchaseUnit: z.string().min(1, 'products:validation.purchaseUnitRequired'),
  saleUnit: z.string().min(1, 'products:validation.saleUnitRequired'),
  lengthMm: optionalNonNegative,
  widthMm: optionalNonNegative,
  heightMm: optionalNonNegative,
  weightKg: optionalNonNegative,
});

export type EditMainInfoFormData = z.infer<typeof editMainInfoSchema>;

export const editClassificationSchema = z
  .object({
    subsidiaryId: z.string().min(1, 'products:validation.subsidiaryRequired'),
    includeChildren: z.boolean(),
    categoryId: z.string().min(1, 'products:validation.categoryRequired'),
    oemNo: z.string().trim().optional(),
    mpn: z.string().trim().optional(),
    upcCode: z.string().trim().optional(),
    countryOfManufacture: z.string().trim().optional(),
    // ROW 68 (FIELD-SIGNOFF F4 §2-Fix3) — 製造商 joins its Manufacturing-group
    // neighbours (countryOfManufacture / faSubCategory) in the chunk edit.
    manufacturer: z.string().trim().nullable().optional(),
    // S2.1 — DD!69 manufacturing sub-classification.
    faSubCategory: z.string().trim().optional(),
    // S2.1 Car Details (DD!31 / DD!32).
    variant: z.string().trim().optional(),
    extractYear: optionalYear,
    // S2.1 C108 age family — year inputs only; `ageGroup` itself is
    // system-computed (pure `computeAgeGroup`) and NEVER a form field.
    ageStartYear: optionalYear,
    ageEndYear: optionalYear,
  })
  .refine(
    (data) =>
      data.ageStartYear == null || data.ageEndYear == null || data.ageEndYear >= data.ageStartYear,
    {
      message: 'products:validation.ageYearRange',
      path: ['ageEndYear'],
    }
  );

export type EditClassificationFormData = z.infer<typeof editClassificationSchema>;

export const editDescriptionsSchema = z.object({
  salesDescription: z.string().trim().optional(),
  purchaseDescription: z.string().trim().optional(),
  customsDescription: z.string().trim().optional(),
  /** DD!46 採購單備註 (0708-J Should, Y-S3) — S0.3 drift law: schema + defaults + hydrate. */
  purchaseOrderNote: z.string().trim().optional(),
});

export type EditDescriptionsFormData = z.infer<typeof editDescriptionsSchema>;

const COSTING_METHODS = ['FIFO', 'MOVING_AVERAGE', 'STANDARD', 'WEIGHTED_AVERAGE'] as const;

export const editCommercialSchema = z
  .object({
    costingMethod: z.enum(COSTING_METHODS).nullable(),
    basePrice: optionalNonNegative,
    currency: z.string().min(1, 'products:validation.currencyRequired'),
    minQty: optionalNonNegative,
    /** DD!100 (0708-J Must) — unit qualifier of `minQty` (S2.2). */
    minQtyUnit: z.string().nullable().optional(),
    maxQty: optionalNonNegative,
    /** DD!102 (0708-J Must) — unit qualifier of `maxQty` (S2.2). */
    maxQtyUnit: z.string().nullable().optional(),
    itemDefinedCost: optionalNonNegative,
    /** DD!98 (0708-J Must) — unit qualifier of `itemDefinedCost` (S2.2). */
    itemDefinedCostUnit: z.string().nullable().optional(),
  })
  .refine((data) => data.maxQty == null || data.minQty == null || data.maxQty >= data.minQty, {
    message: 'products:validation.maxQtyBelowMin',
    path: ['maxQty'],
  });

export type EditCommercialFormData = z.infer<typeof editCommercialSchema>;

export const addVendorLineSchema = z.object({
  vendorId: z
    .number({
      required_error: 'products:validation.vendorRequired',
      invalid_type_error: 'products:validation.vendorRequired',
    })
    .min(1, 'products:validation.vendorRequired'),
  vendorSku: z.string().trim().optional(),
  unitCost: z
    .number({
      required_error: 'products:validation.unitCostRequired',
      invalid_type_error: 'products:validation.unitCostRequired',
    })
    .min(0, 'products:validation.nonNegative'),
  currency: z.string().min(1, 'products:validation.currencyRequired'),
  /**
   * 訂購單位 (W7-B3) — optional capture from the product UNIT_VALUES family;
   * display-only, NO qty conversion against stockUnit (decision-gated — Q5).
   */
  orderUnit: z.string().optional(),
  isPrimary: z.boolean(),
});

export type AddVendorLineFormData = z.infer<typeof addVendorLineSchema>;

export const scnLinkSchema = z
  .object({
    mode: z.enum(['join', 'create']),
    scn: z.string().trim().optional(),
    partnerProductId: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mode === 'join' && !data.scn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'products:validation.scnGroupRequired',
        path: ['scn'],
      });
    }
    if (data.mode === 'create' && !data.partnerProductId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'products:validation.scnPartnerRequired',
        path: ['partnerProductId'],
      });
    }
  });

export type ScnLinkFormData = z.infer<typeof scnLinkSchema>;
